import { SocialAuthService, SocialUser } from "@abacritt/angularx-social-login";
import { isPlatformBrowser } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatDialog, MatDialogRef, MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Router } from "@angular/router";
import {
  SocialSignIn,
  SocialSignInResponse,
} from "@services/types/social-authentication.type";
import { SocialLoginComponent } from "@shared/components/modal-login/social-login/social-login.component";
import { InputOtpModule } from "primeng/inputotp";
import { filter, finalize, interval, Subject, Subscription, take, takeUntil } from "rxjs";
import { AppleAuthService } from "../../../app/services/apple-auth.service";
import { AuthService } from "../../../app/services/auth.service";
import { LocalStorageService } from "../../../app/services/local-storage.service";
import { SnackBarService } from "../../../app/services/snack-bar.service";
import { LoginRequestBody } from "../../../app/services/types/login-request.type";
import { ButtonComponent } from "../button/button.component";
import { LoadingComponent } from "../loading/loading.component";
import { ModalConfirmationComponent } from "../modal-confirmation/modal-confirmation.component";
import { ModalPasswordRecoveryComponent } from "../modal-password-recovery/modal-password-recovery.component";
import { ModalSignupComponent } from "../modal-signup/modal-signup.component";
import { PageHeaderComponent } from "../page-header/page-header.component";
import { LoginForms } from "./types/login-forms.type";

@Component({
    selector: "app-modal-login",
    imports: [
    ReactiveFormsModule,
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,
    ButtonComponent,
    PageHeaderComponent,
    LoadingComponent,
    SocialLoginComponent,
    InputOtpModule,
    FormsModule,
],
    providers: [BrowserAnimationsModule],
    templateUrl: "./modal-login.component.html",
    styleUrl: "./modal-login.component.scss"
})
export class ModalLoginComponent implements OnInit, OnDestroy {
  @Output() userLogged = new EventEmitter<boolean>();
  loginForms!: FormGroup;
  errorMessage = "";

  isLoading = false;
  mfaChallengeId: string | null = null;
  mfaCode = "";
  mfaChannels: string[] = [];
  isMfaTimerActive = false;
  mfaTimer = 60;
  private mfaTimerSubscription?: Subscription;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly dialog = inject(MatDialog);
  readonly dialogRef = inject(MatDialogRef<ModalLoginComponent>);
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly appleAuthService = inject(AppleAuthService);
  private destroy$ = new Subject<void>();

  constructor(
    private readonly authService: AuthService,
    private readonly formBuilder: FormBuilder,
    private readonly snackbarService: SnackBarService,
    private localStorageService: LocalStorageService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.loginForms = this.formBuilder.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", [Validators.required, Validators.minLength(6)]],
    });

    this.clearSocialAuthCache().finally(() => this.subscribeToAuthState());
  }

  ngOnDestroy(): void {
    this.stopMfaTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get mfaPending(): boolean {
    return !!this.mfaChallengeId;
  }

  get mfaChannelsLabel(): string {
    const labels: Record<string, string> = {
      email: "e-mail",
      sms: "SMS",
      whatsapp: "WhatsApp",
    };
    return this.mfaChannels.map((channel) => labels[channel] ?? channel).join(", ");
  }

  onSubmit() {
    if (this.loginForms.valid) {
      this.isLoading = true;
      const { email, password }: LoginForms = { ...this.loginForms.value };
      const signupRequest: LoginRequestBody = {
        user: {
          email: email,
          password: password,
        },
      };

      this.authService
        .login(signupRequest)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: (responseLogin) => {
            if (responseLogin?.status?.data?.mfa_required) {
              this.mfaChallengeId = responseLogin.status.data.challenge_id ?? null;
              this.mfaChannels = responseLogin.status.data.channels ?? [];
              this.mfaCode = "";
              this.startMfaTimer();
              this.snackbarService.openSnackBar(
                "Enviamos um código de verificação para concluir o login.",
                "Fechar",
                "success",
              );
              return;
            }

            if (
              isPlatformBrowser(this.platformId) &&
              responseLogin?.status?.data?.user
            ) {
              this.localStorageService.setItem(
                "current_user",
                JSON.stringify(responseLogin.status.data.user),
              );
            }

            this.finishLogin();
          },
          error: (_error) => {
            this.errorMessage = "Email não confirmado ou senha invalida!";
            this.snackbarService.openSnackBar(
              this.errorMessage,
              "Fechar",
              "error",
            );
          },
        });
    } else {
      this.snackbarService.openSnackBar(
        "Formulario não tem dados validos",
        "Fechar",
        "error",
      );
    }
  }

  continueLoginWithGoogle() {
    this.dialogRef.close();
  }

  // handleSocialLogin() {
  //   this.dialogRef.close();
  // }

  openModalSignUp() {
    this.dialogRef.close();
    this.dialog.open(ModalSignupComponent, {
      width: '800px',
      maxWidth: '95vw',
    });
  }

  forgotPassword() {
    this.dialog.open(ModalPasswordRecoveryComponent, {
      width: '500px',
    });
  }

  verifyMfa() {
    if (!this.mfaChallengeId || this.mfaCode.length !== 6) {
      this.snackbarService.openSnackBar(
        "Informe o código de 6 dígitos.",
        "Fechar",
        "error",
      );
      return;
    }

    this.isLoading = true;
    this.authService
      .verifyLoginMfa(this.mfaChallengeId, this.mfaCode)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => this.finishLogin(),
        error: (error) => {
          this.snackbarService.openSnackBar(
            error?.error?.error ?? "Código inválido. Tente novamente.",
            "Fechar",
            "error",
          );
        },
      });
  }

  resendMfa() {
    if (!this.mfaChallengeId || this.isMfaTimerActive) {
      return;
    }

    this.isLoading = true;
    this.authService
      .resendLoginMfa(this.mfaChallengeId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.mfaCode = "";
          this.startMfaTimer();
          this.snackbarService.openSnackBar(
            "Novo código enviado.",
            "Fechar",
            "success",
          );
        },
        error: (error) => {
          this.snackbarService.openSnackBar(
            error?.error?.error ?? "Não foi possível reenviar o código.",
            "Fechar",
            "error",
          );
        },
      });
  }

  backToPassword() {
    this.mfaChallengeId = null;
    this.mfaCode = "";
    this.mfaChannels = [];
    this.stopMfaTimer();
  }

  private finishLogin() {
    this.snackbarService.openSnackBar(
      "Login feito com sucesso!",
      "Fechar",
      "success",
    );
    this.userLogged.emit(true);
    this.dialogRef.close(true);
  }

  private startMfaTimer() {
    this.stopMfaTimer();
    this.isMfaTimerActive = true;
    this.mfaTimer = 60;
    this.mfaTimerSubscription = interval(1000).subscribe(() => {
      this.mfaTimer--;
      if (this.mfaTimer <= 0) {
        this.stopMfaTimer();
      }
    });
  }

  private stopMfaTimer() {
    this.isMfaTimerActive = false;
    this.mfaTimerSubscription?.unsubscribe();
    this.mfaTimerSubscription = undefined;
  }

  resendConfirmation() {
    const dialogRef = this.dialog.open(ModalConfirmationComponent, {
      width: '600px',
      data: null,
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("Modal fechado!", result);
    });
  }

  handleLoginWithSocialMedia(provider: string) {
    this.socialAuthService.signIn(provider);
  }

  async handleLoginWithApple() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    try {
      const { identityToken, name, lastName } =
        await this.appleAuthService.signIn();

      const payload: SocialSignIn = {
        provider: "apple",
        token: identityToken,
        name,
        last_name: lastName,
      };

      this.authService
        .socialSignIn(payload)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe((response: SocialSignInResponse | null) => {
          if (!response) {
            return;
          }
          this.dialogRef.close();

          if (!response.status.data.user.phone_number) {
            this.router.navigateByUrl("/finish-signup");
          }
        });
    } catch (error) {
      this.isLoading = false;
      const message =
        error instanceof Error
          ? error.message
          : "Não foi possível entrar com a Apple. Tente novamente.";
      this.snackbarService.openSnackBar(message, "Fechar", "error");
    }
  }

  private clearSocialAuthCache(): Promise<unknown> {
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }
    return this.socialAuthService.signOut().catch(() => undefined);
  }

  private subscribeToAuthState() {
    this.socialAuthService.authState
      .pipe(
        takeUntil(this.destroy$),
        filter((user): user is SocialUser => !!user),
        take(1),
      )
      .subscribe((user) => {
        this.handleSocialLogin(user);
      });
  }

  private getProviderToken(user: SocialUser) {
    switch (user.provider) {
      case "GOOGLE":
        return user.idToken ?? "";
      default:
        return user.idToken ?? user.authToken ?? "";
    }
  }

  private handleSocialLogin(user: SocialUser): void {
    if (this.isLoading) return;
    this.isLoading = true;

    const payload: SocialSignIn = {
      provider: user.provider?.toLowerCase() ?? "",
      token: this.getProviderToken(user),
    };

    this.authService
      .socialSignIn(payload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.clearSocialAuthCache();
        }),
      )
      .subscribe((response: SocialSignInResponse | null) => {
        if (!response) {
          return;
        }
        this.dialogRef.close();

        if (!response.status.data.user.phone_number) {
          this.router.navigateByUrl("/finish-signup");
        }
      });
  }
}
